import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type IconName,
  type Omittable,
  ROW_RUNGS,
} from "@salestrak/ui";
import {
  customFieldValuesEqual,
  dataToField,
  fieldDto,
} from "@salestrak/custom-fields";

import {
  OptionChip,
  OptionValueText,
  resolveSelectedOptions,
  showsOptionColors,
} from "#app/components/custom-fields/option-visuals";
import { isCustomFieldValueEmpty } from "#app/components/custom-fields/custom-field-renderers";
import { CustomFieldValueCell } from "#app/components/custom-fields/custom-field-value-cell";
import { isFlaggedPhoneValue } from "#app/components/custom-fields/invalid-phone-marker";
import {
  OptionSelector,
  OptionSelectorTrigger,
  type OnCreateOption,
} from "#app/components/custom-fields/option-picker";
import type { OptionActions } from "#app/lib/hooks/use-custom-field-mutations";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { useIsClipped } from "#app/components/clipped-readout-text";
import { CARD_LABEL_RAIL_CAP, CARD_ROW_CONTAINER_CLASS } from "./card-label-rail";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";

// ---------------------------------------------------------------------------
// Kanban card custom-field TOKENS (the settled convergence design, 2026-07-23
// mockup rounds 5–8, ported from profile-v2). Custom fields are the opposite
// of card PROPERTIES: an empty property keeps an icon action in the footer,
// an empty custom field doesn't show at all. Valued fields render as
// two-segment tokens — a label segment (field name, or the type glyph in
// icon mode) fused to a value segment — flowing in a wrap between the
// property rows and the footer spacing. A multi-select field carries all
// its option chips inside ONE token.
//
// Card grammar: wash on hover = editable · anything clipped reads out in
// full on hover (values AND names) · empty waits in the footer.
//
// Option fields open the shared picker and checkboxes toggle in place; every
// other type EDITS IN ITS TOKEN through the same `CustomFieldValueCell` the
// drawer's property rows and the table's columns render (`surface="card"`):
// the phone editor with its flag, the email/url live ring and commit gate,
// money grouping — one editing UX per type, whatever surface it sits on. The
// TOKEN is the cell's chrome: its edge turns brand while the cell is active
// and destructive while the draft is invalid (or a settled phone value is
// flagged), the way a ClickUp card row tints, instead of a second ring
// inside the token's border.
//
// Which fields show, and in which of the shapes below, is the saved view's
// `config.card` — edited on the panel's Fields page (`components/view-options`)
// and shared with everyone who opens the view.
// ---------------------------------------------------------------------------

/** `text` spells field names in the label segment; `icon` collapses it to
 *  the type glyph with the name in a tooltip. Covers PROPERTIES too: in
 *  text mode the card's property rows (address/stage/type/dates) wear the
 *  same name-labeled token shell; icon mode keeps their plain glyph chips
 *  (the glyph already carries the identity). */
export type FieldLabelMode = "text" | "icon";
/** How a TEXT label sits against its value in list layout: `horizontal`
 *  = beside it on the view's shared rail (default); `vertical` = on its own
 *  line above it, the token a two-line column. Icon mode ignores it. */
export type TextLabelLayout = "horizontal" | "vertical";
/** `wrap` = tokens flow in a mosaic; `list` = one full-width token per line
 *  (in text mode the label segments form ONE rail sized per view — see
 *  `card-label-rail.ts`). */
export type FieldLayout = "wrap" | "list";

/** Display options shared by every surface that renders an issue card. */
export interface CardDisplayOptions {
  fieldLabelMode: FieldLabelMode;
  /** Text labels only — a sub-setting of `fieldLabelMode: "text"`. */
  textLabelLayout: TextLabelLayout;
  fieldLayout: FieldLayout;
  // BODY properties are user-hideable/reorderable. Footer FURNITURE
  // (comments, assignee) has fixed seats — never in the popover, no flags:
  // the footer is the card's chrome, like the title. Age is the one footer
  // resident with a toggle (Spencer, default ON) — hideable but its SEAT is
  // fixed (an itemOrder position for "age" changes nothing).
  showAddress: boolean;
  showTeams: boolean;
  showStage: boolean;
  showWorkflow: boolean;
  showProjectType: boolean;
  showCreated: boolean;
  showUpdated: boolean;
  showAge: boolean;
  /** Custom-field ids toggled ON the card. Custom fields are OPT-IN (Spencer:
   *  "by default no custom fields should be shown — these are up to the
   *  user") — an inclusion list, so newly created fields never appear on
   *  anyone's cards until they choose them. */
  shownFieldIds: string[];
  /** Also render valueless custom fields (as "—" tokens). Properties are
   *  NOT affected — empty properties already live in the footer's actions. */
  showEmptyFields: boolean;
  /**
   * User-dragged render order of the card's shown BODY items — property
   * keys ("address"/"stage") and custom-field ids, mixed. Items absent from
   * the list (new fields, never-dragged setups) keep their default position
   * AFTER the ordered ones; stale keys are ignored.
   */
  itemOrder: string[];
}

export const DEFAULT_CARD_DISPLAY_OPTIONS: CardDisplayOptions = {
  // Icons + stacked fields by default (Spencer) — names stay one hover away
  // via the tooltip; "Text" and unstacked (wrap) are the opt-ins.
  fieldLabelMode: "icon",
  // Beside the value when Text is picked; Vertical is the opt-in.
  textLabelLayout: "horizontal",
  fieldLayout: "list",
  showAddress: true,
  // Teams default ON — team responsibility is the board's working currency.
  showTeams: true,
  // Stage/type/dates default OFF on the board — the board is usually grouped
  // by stage (redundant on the card), and dates are list-shaped detail.
  showStage: false,
  showWorkflow: false,
  showProjectType: false,
  showCreated: false,
  showUpdated: false,
  showAge: true,
  shownFieldIds: [],
  showEmptyFields: false,
  itemOrder: [],
};

/** Card body-property keys the popover can show/hide/reorder — the itemOrder
 *  keys for the non-custom-field properties. */
export type CardPropertyKey =
  | "address"
  | "teams"
  | "stage"
  | "workflow"
  | "projectType"
  | "created"
  | "updated";

/** Stable-sort by the user's dragged order: ordered keys first (in order),
 *  everything else keeps its relative default position after them. */
export function sortByItemOrder<T>(
  items: T[],
  keyOf: (item: T) => string,
  order: string[],
): T[] {
  if (order.length === 0) return items;
  const index = new Map(order.map((key, i) => [key, i]));
  return [...items].sort((a, b) => {
    const ai = index.get(keyOf(a));
    const bi = index.get(keyOf(b));
    if (ai == null && bi == null) return 0;
    if (ai == null) return 1;
    if (bi == null) return -1;
    return ai - bi;
  });
}

/**
 * The card body is a borderless property list: no hairlines, no glyph band.
 * The glyph column starts where the title starts, 28px rows run on a 2px gap
 * (30px pitch), and the hover wash is the only chrome. The body pulls its
 * inset to 4px so the wash reaches nearly to the card edge (the title rail and
 * footer keep their 12px/8px seats — see `issue-card.tsx`).
 *
 * Every token HUGS its content — glyph plus value, in list layout too. The
 * rest of each row is the card, and a press there opens the project. With a
 * dozen valued rows a full-width token left the title rail and the 2px seams
 * as the only open surface (about a quarter of the card); hugged, the open
 * surface is about two thirds and the wash is exactly the size of the edit
 * target. Nothing moves at rest (bare mode paints no edge); the token only
 * fills its row while its inline editor is live, so the editor keeps its
 * width. The value segment holds a 44px floor so a checkbox or an em dash is
 * still a target.
 */
export const LABEL_SEGMENT_MAX_WIDTH = 104;
/** Narrowest value segment: a bare checkbox or "—" still needs a press
 *  target wider than a glyph. */
const VALUE_SEGMENT_MIN_WIDTH = 44;
/** Stamped on every element inside a card that OWNS its press (a token, a
 *  footer action, the assignee): the card's open cue (the title's underline)
 *  stands down while the pointer is over one. The library KanbanCard stamps
 *  its control cluster with the same attribute. */
export const OWNS_PRESS_ATTR = "data-owns-press";
/** Is the token a two-line name-over-value column on this surface? Text
 *  labels, list layout, and the view's "Label layout" set to Vertical. */
export function isCardLabelStacked(
  labelMode: FieldLabelMode,
  labelLayout: TextLabelLayout | undefined,
  layout: FieldLayout,
): boolean {
  return (
    labelMode === "text" && labelLayout === "vertical" && layout === "list"
  );
}

/** Air between list rows. Bare side-by-side rows run 2px apart (28px rows
 *  on a 30px pitch); stacked name-over-value tokens run 8px apart, so the
 *  gap INSIDE a pair (name to value) is smaller than the gap between pairs
 *  and each name reads as its value's, not the row above's (Spencer:
 *  "feels very crammed" at 2px). */
export function cardListRowGap(
  labelMode: FieldLabelMode,
  labelLayout: TextLabelLayout | undefined,
  layout: FieldLayout,
): "2xs" | "sm" {
  return isCardLabelStacked(labelMode, labelLayout, layout) ? "sm" : "2xs";
}
/** Every row's label segment is one 28px-tall box pinned to the row's top,
 *  so the name (or glyph) centers on the VALUE'S FIRST LINE — a wrapping
 *  chip set hangs from its name instead of floating it mid-row. */
export const LABEL_SEGMENT_HEIGHT = ROW_RUNGS.compactControl;
/** Icon-mode glyph rail. Bordered: a 28px band the 16px glyph centers in.
 *  Bare: the glyph sits at the rail's 8px lead and the rail ENDS at the
 *  glyph's trailing edge (8 + 16 = 24) — the value segment's own 8px inset
 *  is the whole glyph→value gap, so a value starts 8px after the glyph
 *  whether it is bare text, a hosted cell, an option chip or a kind mark. */
const LIST_GUTTER_ICON = 24;

// Photos values are opaque media ids — meaningless as card text. Skipped
// until the card grows a media strip.
const HIDDEN_CARD_FIELD_TYPES = new Set(["photos"]);

export interface CardCustomField {
  field: CustomFieldDef;
  value: unknown;
}

/** The fields a card renders: valued (plus empties when the display option
 *  asks), in def order, minus hidden types. */
export function getCardCustomFields(
  fields: CustomFieldDef[],
  values: unknown,
  includeEmpty = false,
): CardCustomField[] {
  if (fields.length === 0) return [];
  const map = (values ?? {}) as Record<string, unknown>;
  return fields.flatMap((field) => {
    if (HIDDEN_CARD_FIELD_TYPES.has(field.type)) return [];
    const value = map[field.customFieldId];
    if (!includeEmpty && isCustomFieldValueEmpty(field, value)) return [];
    return [{ field, value }];
  });
}

/** Resolve the workspace field definitions enabled by a card configuration. */
export function getShownCardFieldDefs(
  fields: CustomFieldDef[],
  shownFieldIds: string[],
): CustomFieldDef[] {
  if (shownFieldIds.length === 0) return [];
  const shown = new Set(shownFieldIds);
  return fields.filter((field) => shown.has(field.customFieldId));
}

/** Token count a card's field block renders — ONE token per field (a
 *  multi-select field carries all its option chips inside one expandable
 *  token). Feeds the board's height estimate alongside
 *  {@link getCardCustomFields}. */
export function countCardFieldTokens(fields: CardCustomField[]): number {
  return fields.length;
}

/**
 * Field-edit plumbing, lifted to the card host (one mutation/permission
 * set — never per card) and threaded down bound to the card's issue.
 */
export type CardFieldOps = Omittable<{
  /** Persist one field's value on this card's issue (option arrays for
   *  multi/single select, a boolean for checkbox). */
  onValuesChange: (field: CustomFieldDef, value: unknown) => void;
  /** Inline option creation for the pickers (create.items-gated inside
   *  OptionSelector). */
  onCreateOption?: OnCreateOption;
  /** In-picker option management (managers only — the board gates on
   *  custom_fields.manage before threading this down). */
  optionActions?: OptionActions;
}>

export type IssueCardFieldsProps = Omittable<{
  /** Pre-filtered valued fields (see {@link getCardCustomFields}). */
  fields: CardCustomField[];
  /** Space above the block: `item` = the 4px object rhythm (a property row
   *  sits above), `none` = the block rides the title rail. */
  lead?: "none" | "item";
  /** Viewer may edit values on THIS issue (card edit access). */
  canApply: boolean;
  fieldOps: CardFieldOps;
  /** Label segment content (display options; default `text`). */
  labelMode?: FieldLabelMode;
  /** Text-label layout (display options; default `horizontal`). */
  labelLayout?: TextLabelLayout;
  /** Token flow (display options; default `wrap`). */
  layout?: FieldLayout;
  /** List+text: the view's measured label rail in px (`useCardLabelRail`). */
  labelRail?: number;
}>;

export function IssueCardFields({
  fields,
  lead = "none",
  canApply,
  fieldOps,
  labelMode = "text",
  labelLayout = "horizontal",
  layout = "wrap",
  labelRail,
}: IssueCardFieldsProps) {
  if (fields.length === 0) return null;
  const isList = layout === "list";
  // Bare list rows run a 30px pitch: 28px rows on a 2px gap (a hairline of
  // air between the sunken/tinted rows); the bordered tokens keep the 4px
  // object rhythm.

  // List layout: each token sits in its own ROW box, never as a direct
  // child of the column — a column stretches its children to the full
  // width, and the token has to hug its content so the rest of the row
  // stays the card's press. A row box lets the token shrink to the width
  // available (a long value truncates, a chip set wraps) without stretching
  // a short one. The row is also the query container the text-mode label
  // rail's cap resolves against (see `card-label-rail.ts`).
  const seat = (key: string, token: React.ReactNode) =>
    isList ? (
      <Box
        key={key}
        direction="row"
        minWidth={0}
        fill="width"
        className={CARD_ROW_CONTAINER_CLASS}
      >
        {token}
      </Box>
    ) : (
      <React.Fragment key={key}>{token}</React.Fragment>
    );

  return (
    // No horizontal inset — token borders align flush with the property
    // chips' borders at the card body edge (Spencer: fields read "more
    // inset" than properties otherwise).
    <Box
      direction={isList ? "column" : "row"}
      wrap={!isList}
      align={isList ? undefined : "start"}
      gap={isList ? cardListRowGap(labelMode, labelLayout, layout) : "xs"}
      paddingTop={
        lead === "item"
          ? isList
            ? cardListRowGap(labelMode, labelLayout, layout)
            : "xs"
          : undefined
      }
    >
      {fields.map(({ field, value }, index) => {
        const key = `${field.customFieldId}-${index}`;
        const isOptionField = field.type === "labels" || field.type === "select";
        if (isOptionField) {
          const multiple = field.type === "labels";
          const selected = resolveSelectedOptions(field, value);
          return seat(
            key,
            <FieldToken
              field={field}
              picker={{ value, canApply, fieldOps, single: !multiple }}
              labelMode={labelMode}
              labelLayout={labelLayout}
              layout={layout}
              labelRail={labelRail}
            >
              {selected.length === 0 ? (
                // Only reachable with "Show empty fields" on — a "—" token
                // that still opens the picker.
                <Text size="sm" variant="faint">
                  {NONE_OPTION_LABEL}
                </Text>
              ) : !showsOptionColors(field) ? (
                // Colors off: plain text, never colorless chips.
                <OptionValueText options={selected} />
              ) : multiple ? (
                // ONE token per field (reference parity): every selected
                // option's chip lives inside the token's value segment,
                // wrapping — the token keeps its min-height and GROWS with
                // its chips. Never one token per value (list mode = one line
                // per FIELD). 4px above and below: the first chip row sits
                // where a single value's line sits (the 28px floor), so the
                // top-pinned label centers on it.
                <Box
                  direction="row"
                  align="center"
                  wrap
                  gap="xs"
                  paddingY="xs"
                  shrink
                  minWidth={0}
                >
                  {selected.map((opt, optIndex) => (
                    <OptionChip
                      key={`${opt.value}-${optIndex}`}
                      label={opt.label}
                      color={opt.color}
                    />
                  ))}
                </Box>
              ) : (
                <OptionChip
                  label={selected[0]!.label}
                  color={selected[0]!.color}
                  tone="tonal"
                />
              )}
            </FieldToken>,
          );
        }
        if (field.type === "checkbox") {
          return seat(
            key,
            <CheckboxFieldToken
              field={field}
              value={value}
              canApply={canApply}
              fieldOps={fieldOps}
              labelMode={labelMode}
              labelLayout={labelLayout}
              layout={layout}
              labelRail={labelRail}
            />,
          );
        }
        return seat(
          key,
          <CellFieldToken
            field={field}
            value={value}
            canApply={canApply}
            fieldOps={fieldOps}
            labelMode={labelMode}
            labelLayout={labelLayout}
            layout={layout}
            labelRail={labelRail}
          />,
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CardTokenShell — the bare two-segment token: bordered shell, muted label
// segment (NAME in text mode, glyph in icon mode), value segment. Shared by
// the custom-field tokens below AND the card's property rows in text label
// mode (the "Field labels" display option covers properties too — address
// and stage/type/dates all wear the same label segment). Callers wrap it in
// their own Popover/Tooltip; as a `render` trigger child the Box takes Base
// UI's injected press (ref travels as a plain prop — React 19).
// ---------------------------------------------------------------------------

type CardTokenShellProps = Omittable<{
  /** Whose glyph identifies the row in ICON label mode. `"type"` (default)
   *  seats the property/field TYPE's glyph in the label rail. `"value"` is
   *  for rows whose value LEADS with its own identity visual (a stage pie,
   *  a workflow mark, a team tile): the label segment yields entirely and
   *  the value's visual becomes the row's glyph, in the same column — one
   *  glyph per row, never two. The name stays one hover away (the token's
   *  tooltip); text label mode is untouched (a name label never doubles). */
  labelGlyph?: "type" | "value";
  /** Field/property NAME — spelled as authored in the label segment. */
  label: string;
  /** Label-segment glyph (icon mode). */
  icon: IconName;
  labelMode: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout: FieldLayout;
  /** List+text: the view's measured rail in px — the segment pins to
   *  `min(rail, CARD_LABEL_RAIL_CAP)` so one gutter runs down every card of
   *  the view. Absent (a lone token outside a card provider), the segment
   *  hugs its name under the same cap. */
  labelRail?: number;
  /** Stretch across the row. A token hugs its content by default (the rest
   *  of the row is the card's press); the shell fills on its own while its
   *  value hosts a live editor (`tone="editing"`), so this is for a caller
   *  whose value must span the row for another reason. */
  fillRow?: boolean;
  /** Label-segment chrome: `filled` = the muted band (custom fields);
   *  `plain` = bare faint text, no band — PROPERTIES, so they keep reading
   *  distinct from user data in text mode (they carry no band in icon mode
   *  either). */
  /** Editable: pointer cursor + the hover wash (the card grammar's "wash on
   *  hover = editable", edge to edge, glyph included). */
  pressable?: boolean;
  /** Applied whenever given, pressable or not — a read-only token with a
   *  copy action inside still has to keep that press off the card. */
  onClick?: (e: React.MouseEvent) => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  /** Edge state for a token whose value segment hosts an editing cell:
   *  `editing` = the brand ring (the cell is live), `invalid` = destructive
   *  (a rejected draft, or a settled value that does not parse). Stamped as
   *  `data-tone` too. */
  tone?: "editing" | "invalid";
  /** The value segment's own 8px inset — the SAME inset a hosted value cell
   *  wears (`surface="card"`), so a chip, a checkbox or an entity tag starts
   *  where a text value starts. Off when the child wears the inset itself (a
   *  value cell pads its rest display and its editor alike). */
  valueInset?: boolean;
  "aria-label"?: string;
  ref?: React.Ref<HTMLDivElement>;
  /** Clip-detection ref for the label segment (the name-readout rule). */
  labelRef?: (node: HTMLElement | null) => void;
  /** Value-segment content. */
  children: React.ReactNode;
}>;

/** The rest-spread is LOAD-BEARING: the shell sits as a Base UI `render`
 *  trigger child, so the trigger's injected props (onClick, ref, aria state)
 *  must land on the outer Box or the press never opens the picker. */
export function CardTokenShell({
  label,
  icon,
  labelGlyph = "type",
  labelMode,
  labelLayout,
  layout,
  labelRail,
  fillRow,
  pressable,
  onClick,
  onPointerDown,
  tone,
  valueInset = true,
  labelRef,
  children,
  ...rest
}: CardTokenShellProps) {
  const isIconMode = labelMode === "icon";
  const isList = layout === "list";
  // The value's own visual is this row's glyph — the rail would double it.
  const valueLeads = isIconMode && labelGlyph === "value";
  // Text mode, list layout, "Label layout: Vertical": the name sits on its
  // own line above the value — no rail, the token is a two-line column.
  const stacked = isCardLabelStacked(labelMode, labelLayout, layout);
  // A live inline editor takes the row: an input hugging a short value
  // would be a few characters wide. The token returns to its content on
  // deactivation; the value's left edge holds still either way.
  const fills = !!fillRow || tone === "editing";
  // The token owns its press when it is editable OR carries its own click
  // guard (a read-only cell token still holds a copy action): either way
  // a press on it is not the card's, so the card's open cue stands down.
  const ownsPress = !!pressable || onClick !== undefined;
  return (
    <Box
      {...rest}
      {...(ownsPress ? { [OWNS_PRESS_ATTR]: "" } : {})}
      direction={stacked ? "column" : "row"}
      align={stacked ? "start" : "stretch"}
      // The row has no edge to tint, so the ROW carries the state: an invalid
      // value at rest tints the whole row destructive (glyph, text and marker
      // already read red), and a live editor drops the row to the sunken well
      // so it reads as a field cut into the card while it is up.
      background={
        tone === "editing"
          ? "sunken"
          : tone === "invalid"
            ? "destructiveSoft"
            : undefined
      }
      data-tone={tone}
      rounded="md"
      overflow="hidden"
      // 28px object floor (card body objects; footer controls stay 24) —
      // the 20px/14px chip step breathes inside, and the token still GROWS
      // with wrapping option chips. List rows keep the same 28px floor (the
      // reference's density) on a 2px gap, the 20px glyph/tag step seated at
      // 4px top and bottom.
      minHeight={28}
      // Hugs its content in BOTH layouts (list rows seat it in a row box
      // that lets it shrink to the width available); only a live editor
      // takes the row.
      shrink
      minWidth={0}
      fill={fills ? "width" : undefined}
      group="token"
      cursor={pressable ? "pointer" : undefined}
      hoverBackground={pressable}
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      {/* Label segment — the NAME by default (self-describing at any
          density); type glyph in icon mode with the name one hover away. A
          value-led row renders NO label segment: its value's own visual sits
          in the glyph column instead. */}
      {valueLeads ? null : (
      <Box
        ref={labelRef}
        direction="row"
        align="center"
        // Pinned to the row's top at the row's one-line height: the name
        // centers on the value's first line, and a wrapping value hangs
        // from it (stretching with the row would float it mid-row).
        alignSelf={stacked ? undefined : "start"}
        // Stacked: the name's own line (16px) on a 4px lead, hugging the
        // token's width; the value's 28px floor sits under it.
        height={stacked ? undefined : LABEL_SEGMENT_HEIGHT}
        paddingTop={stacked ? "2xs" : undefined}
        // Icon mode seats the glyph at the rail's 8px — with the body's 4px
        // inset that is the title's 12px, so glyphs and title share one left
        // edge.
        paddingX={isIconMode ? undefined : "sm"}
        paddingLeft={isIconMode ? "sm" : undefined}
        shrink={false}
        overflow="hidden"
        width={
          isIconMode
            ? LIST_GUTTER_ICON
            : isList && !stacked && labelRail !== undefined
              ? `min(${labelRail}px, ${CARD_LABEL_RAIL_CAP})`
              : undefined
        }
        maxWidth={
          stacked
            ? "100%"
            : isList
              ? isIconMode
                ? undefined
                : CARD_LABEL_RAIL_CAP
              : LABEL_SEGMENT_MAX_WIDTH
        }
      >
        {isIconMode ? (
          // Same 16px step as the property glyphs (age/address) — the smaller
          // xs icons read undersized next to them (Spencer). The row has no
          // band to seat the glyph in, so it steps down to the faint rung — a
          // column of quiet type markers beside the values, not a row of
          // icons competing with them.
          <Icon color={tone === "invalid" ? "destructive" : "faint"} name={icon} size="md" />
        ) : (
          // Sentence case on the xs rung, faint: the name reads as a quiet
          // key beside its value and spends no width on capitals (an
          // uppercased 10px name clips at a dozen characters).
          <Text size="xs" variant="faint" truncate={1}>
            {label}
          </Text>
        )}
      </Box>
      )}
      <Box
        direction="row"
        // A hosted value cell fills the segment's height (the token's 28px
        // floor less its hairlines) instead of centering at content height.
        align={valueInset ? "center" : "stretch"}
        gap="xs"
        paddingX={valueInset ? "sm" : undefined}
        // Stacked: the value keeps the card's 28px object floor under its
        // name — a 20px text line or chip centered in it leaves 4px of air
        // below the name (a 24px row left 2px, and valued rows read crammed
        // while "—" rows did not; Spencer). The 8px row gap stays wider.
        minHeight={stacked ? 28 : undefined}
        shrink
        // Hugs the value, with a floor so a checkbox or an em dash is still
        // a press target; a hosted editor spans the row.
        minWidth={VALUE_SEGMENT_MIN_WIDTH}
        fill={fills ? (stacked ? "width" : "flex") : undefined}
        // Rows put their values on the SECONDARY ink rung, so the
        // title is the card's one full-ink line and a scan lands on names
        // first, values second. Set on the segment, not per value: every
        // inheriting label takes it — text values (address, a field's
        // display text, a date) AND entity chip labels (Stage, Workflow,
        // Project Type, whose colored kind mark still carries the identity)
        // — while a value that paints its own ink (an option chip's tint, a
        // destructive/invalid readout) keeps it. An editor inside paints
        // `text-foreground` itself. Library gap: Box has no ink prop, so the
        // segment names the rung by class.
        className="text-muted-foreground"
      >
        {children}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FieldToken — the custom-field token. Nesting order: Tooltip › selector ›
// the token shell as the `render` trigger child.
// ---------------------------------------------------------------------------

type TokenPicker = Omittable<{
  value: unknown;
  canApply: boolean;
  fieldOps: CardFieldOps;
  single?: boolean;
}>;

/** Option values always travel as the array shape (single included). */
function toOptionValues(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

function FieldToken({
  field,
  children,
  picker,
  cell,
  fillRow,
  labelMode,
  labelLayout,
  layout,
  labelRail,
}: Omittable<{
  field: CustomFieldDef;
  /** Value-segment content. */
  children: React.ReactNode;
  /** Labels/select: the shared option picker on press. */
  picker?: TokenPicker;
  /** The value segment hosts an editing cell (its own trigger, inset and
   *  actions): the shell wears the cell's edge tone, drops its 8px inset,
   *  stops pointer-downs while the cell is live, and stands its name
   *  tooltip down — the cell's validation bubble and editor own the hover. */
  cell?: Omittable<{
    pressable: boolean;
    live: boolean;
    tone?: "editing" | "invalid";
  }>;
  /** Stretch across the row (long text tokens under a name tooltip). */
  fillRow?: boolean;
  labelMode: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout: FieldLayout;
  labelRail?: number;
}>) {
  const meta = getFieldTypeMeta(field.type);
  const isIconMode = labelMode === "icon";
  const { setRef, clipped } = useIsClipped(field.name);
  const [pickerOpen, setPickerOpen] = useState(false);

  const pressable = !!picker?.canApply || !!cell?.pressable;
  // The tooltip wrapper hugs the token like the token hugs its value — a
  // wrapper spanning the row would name the field over the card's own
  // press surface. It spans only while the editor does.
  const fills = !!fillRow || cell?.tone === "editing";

  const token = (
    <CardTokenShell
      label={field.name}
      icon={meta.icon}
      labelMode={labelMode}
      labelLayout={labelLayout}
      layout={layout}
      labelRail={labelRail}
      fillRow={fillRow}
      pressable={pressable}
      // Own the press so it never bubbles to the card press (open issue);
      // the picker opens via the wrapping selector trigger, the cell via its
      // own trigger button. A read-only cell token still owns it — its copy
      // action is a press target. A read-only picker token has none, so its
      // press falls through to the card.
      onClick={pressable || cell ? (e) => e.stopPropagation() : undefined}
      // The board's drag sensor arms on a pointer-down anywhere on the card
      // and starts after 8px of travel — selecting text in a live editor, or
      // pressing inside the cell's picker popup (React bubbles portal events
      // up THIS tree), would drag the card. Stopped only while live: at rest
      // the token is card, and grabbing it drags like any other spot.
      onPointerDown={cell?.live ? (e) => e.stopPropagation() : undefined}
      tone={cell?.tone}
      valueInset={cell === undefined}
      // The cell's trigger carries its own "Edit <name>" label.
      aria-label={pressable && !cell ? `Edit ${field.name}` : undefined}
      labelRef={setRef}
    >
      {children}
    </CardTokenShell>
  );

  const withPicker =
    picker && picker.canApply ? (
      <OptionSelector
        field={field}
        values={toOptionValues(picker.value)}
        multiple={!picker.single}
        onValuesChange={(vals) => picker.fieldOps.onValuesChange(field, vals)}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onCreateOption={picker.fieldOps.onCreateOption}
        optionActions={picker.fieldOps.optionActions}
      >
        <OptionSelectorTrigger render={token} />
      </OptionSelector>
    ) : (
      token
    );

  // Name tooltip: always in icon mode (the name isn't on screen), only when
  // the label segment clips in text mode — one rule: clipped reads out. On a
  // coarse pointer a held press reads it, yielding to a clipped value inside
  // the token (the innermost enabled trigger takes the press).
  if (!isIconMode && !clipped) return withPicker;
  return (
    // Disabled, not unmounted, while the cell is live: swapping the wrapper
    // would remount the token and drop the editor mid-keystroke.
    <Tooltip disabled={cell?.live}>
      <TooltipTrigger
        render={
          // A ROW box: a column would stretch the token across it.
          <Box
            direction="row"
            shrink
            minWidth={0}
            fill={fills ? "width" : undefined}
          >
            {withPicker}
          </Box>
        }
      />
      {/* Left side, same as the card's property tooltips. */}
      <TooltipPopup side="left">
        <Box direction="column" gap="xs">
          <Text size="xs">{field.name}</Text>
          {/* Tooltip surface is fixed dark in both themes — dim via opacity,
              not a theme color that flips in light mode. */}
          <Box className="opacity-70">
            <Text size="xs">Custom Field</Text>
          </Box>
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}

/** Checkbox fields render a real checkbox, never "Yes"/"No" (reference
 *  parity — the drawer already toggles in place). The box always renders,
 *  even unset (null); first press writes `true`, further presses toggle.
 *  Read-only viewers see the state without a press target. */
function CheckboxFieldToken({
  field,
  value,
  canApply,
  fieldOps,
  labelMode,
  labelLayout,
  layout,
  labelRail,
}: {
  field: CustomFieldDef;
  value: unknown;
  canApply: boolean;
  fieldOps: CardFieldOps;
  labelMode: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout: FieldLayout;
  labelRail?: number | undefined;
}) {
  const checked = value === true;
  return (
    <FieldToken
      field={field}
      labelMode={labelMode}
      labelLayout={labelLayout}
      layout={layout}
      labelRail={labelRail}
    >
      {/* The wrapper owns the propagation guard against the card press;
          the Checkbox itself is the toggle target (16px, the drawer's step).
          A flex row, never a block: a block wrapper seats the inline box on
          its 24px text line and the token grows past the 28px floor every
          other row holds. Read-only: controlled `checked` with no handler
          stays put. */}
      <Box
        direction="row"
        align="center"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Checkbox
          checked={checked}
          aria-label={`Toggle ${field.name}`}
          onCheckedChange={
            canApply
              ? () => fieldOps.onValuesChange(field, !checked)
              : undefined
          }
        />
      </Box>
    </FieldToken>
  );
}

/**
 * Every non-option, non-checkbox type: the shared value cell in the token's
 * value segment — the type's own inline editor or picker, its validation, its
 * hover copy action. The host state mirrors the table's cell host: one
 * active flag, one overlay mirror feeding `highlighted`, values converted to
 * field space on the way in and DTO space on the way out.
 */
function CellFieldToken({
  field,
  value,
  canApply,
  fieldOps,
  labelMode,
  labelLayout,
  layout,
  labelRail,
}: {
  field: CustomFieldDef;
  /** Stored (DTO-space) value off the issue. */
  value: unknown;
  canApply: boolean;
  fieldOps: CardFieldOps;
  labelMode: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout: FieldLayout;
  labelRail?: number | undefined;
}) {
  const [isActive, setIsActive] = useState(false);
  // Mirror of the cell's overlay/touch-reveal state; feeds `highlighted` so
  // the cell's own dismiss lifecycle works without a dialog-style container.
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Field-space conversion, exactly as the drawer's formValues and the
  // table's cells do it. An absent value stays `undefined` — the cell renders
  // its empty marker.
  const fieldValue = useMemo(
    () =>
      value === undefined ? undefined : dataToField(field.type, value).fieldValue,
    [field.type, value],
  );
  const hasValue = !isCustomFieldValueEmpty(field, fieldValue);
  // A settled phone that does not parse reads red at rest (value text +
  // marker inside the cell) — the token edge says the same.
  const flaggedPhone = isFlaggedPhoneValue(field, fieldValue, hasValue);

  const handleUpdate = useCallback(
    (next: unknown) => {
      // `undefined` is the remove signal and travels through untouched.
      if (next === undefined) {
        fieldOps.onValuesChange(field, undefined);
        return;
      }
      // fieldDto coerces rather than throws.
      const dto = fieldDto(field.type, next);
      // The token commits on every blur, edited or not: the value the card
      // already holds is not a write.
      if (customFieldValuesEqual(value, dto)) return;
      fieldOps.onValuesChange(field, dto);
    },
    [field, fieldOps, value],
  );
  const handleActivate = useCallback(() => setIsActive(true), []);
  const handleDeactivate = useCallback(() => setIsActive(false), []);

  const live = isActive || overlayOpen;
  // The row has no edge, and the fill is the state of the ROW, not the draft:
  // a live editor sits in the sunken well whatever it holds (the red text and
  // the validation bubble already say the draft is wrong), and the
  // destructive tint is the REST-state flag on a settled bad value.
  const tone = isActive
    ? "editing"
    : validationError !== null || flaggedPhone
      ? "invalid"
      : undefined;

  return (
    <FieldToken
      field={field}
      cell={{ pressable: canApply, live, tone }}
      labelMode={labelMode}
      labelLayout={labelLayout}
      layout={layout}
      labelRail={labelRail}
    >
      <CustomFieldValueCell
        surface="card"
        field={field}
        value={fieldValue}
        // No height: the cell fills the token's value segment (the shell
        // stretches it), so the token stays at its 28px floor.
        // The token clips its own corners; the row-style end bleed would be
        // cut square.
        bleedEnd={false}
        isActive={isActive}
        highlighted={live}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        onOverlayChange={setOverlayOpen}
        onValidationErrorChange={setValidationError}
        onUpdate={handleUpdate}
        readOnly={!canApply}
        onCreateOption={fieldOps.onCreateOption}
        optionActions={fieldOps.optionActions}
      />
    </FieldToken>
  );
}

export type { OnCreateOption };
